import { useCallback } from "react";
import { format } from "date-fns";

import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { showError, showSuccess, showWarning } from "src/utils/toast/toastUtil";

import {
	deleteTQC2011_R01,
	downloadCertificatePdf_TQC2011_R01,
	downloadExcel_TQC2011_R01,
	getTQC2011_R01AllCount,
	getTQC2011_R01Detail,
	getTQC2011_R01List,
	saveTQC2011_R01,
	TQC2011_R01Params,
} from "./Api_TQC2011_R01";
import {
	TQC2011_R01Dto,
	TQC2011_R01HeaderDto,
	TQC2011_R01_VALUE_FIELDS,
} from "./Dto_TQC2011_R01";
import {
	defaultTQC2011_R01Info,
	TQC2011_R01Filter,
	useTQC2011_R01Store,
} from "./Store_TQC2011_R01";

const toYmd = (date?: Date) => date ? format(date, "yyyy-MM-dd") : undefined;

const toDate = (value: Date | string | undefined) => {
	if (!value) return undefined;
	return value instanceof Date ? value : new Date(`${value}T00:00:00`);
};

const getParams = (filter: TQC2011_R01Filter): TQC2011_R01Params => ({
	compCd: filter.compCd || undefined,
	startDate: toYmd(filter.dateRange?.from),
	endDate: toYmd(filter.dateRange?.to),
	workplaceCd: filter.workplaceCd || undefined,
	itemCd: filter.itemCd || undefined,
	productEmp: filter.productEmp || undefined,
});

const getNoDetailMessage = (row: TQC2011_R01HeaderDto, inspectType: string) => {
	if (!row.itemNm) {
		return `품목 [${row.itemCd}] 이(가) 품목마스터에 등록되어 있지 않습니다.`;
	}
	if (!row.itemGroup) {
		return `품목 [${row.itemNm}] 에 품목그룹이 지정되지 않았습니다.`;
	}
	return `품목 [${row.itemNm}] 에 적용된 ${inspectType} 검사항목이 없습니다. 검사항목과 품목별 검사항목 등록 내용을 확인해주세요.`;
};

export const Content_TQC2011_R01 = () => {
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { inspectionTypeList } = useCommonCodeStore();
	const {
		TQC2011_R01Filter,
		setTQC2011_R01PastFilter,
		TQC2011_R01Info,
		setTQC2011_R01Info,
		setTQC2011_R01List,
		setAllCount,
		selectedTQC2011_R01,
		setSelectedTQC2011_R01,
		initSelectedTQC2011_R01,
		TQC2011_R01DetailList,
		setTQC2011_R01DetailList,
		setOriginalTQC2011_R01DetailList,
		setScrollTop,
		setDetailScrollTop,
		initData,
	} = useTQC2011_R01Store();

	const setDetailData = useCallback((
		row: TQC2011_R01HeaderDto,
		result: TQC2011_R01Dto,
	) => {
		const header = {
			...row,
			...result.header,
			valueIndex: row.valueIndex,
			inspectDt: toDate(result.header.inspectDt),
		};
		const details = result.detailList ?? [];
		setSelectedTQC2011_R01(header);
		setTQC2011_R01Info({
			inspectType: header.inspectType,
			inspectDt: header.inspectDt as Date | undefined,
			inspectorEmp: header.inspectorEmp ?? "",
			inspectorEmpNm: header.inspectorEmpNm ?? "",
			approvorEmpno: header.approvorEmpno ?? "",
			approvorEmpNm: header.approvorEmpNm ?? "",
			inspectRemark: header.inspectRemark ?? "",
		});
		setTQC2011_R01DetailList(details);
		setOriginalTQC2011_R01DetailList(details.map((detail) => ({ ...detail })));
	}, [
		setOriginalTQC2011_R01DetailList,
		setSelectedTQC2011_R01,
		setTQC2011_R01DetailList,
		setTQC2011_R01Info,
	]);

	const loadDetail = useCallback(async (
		row: TQC2011_R01HeaderDto,
		inspectType: string,
	) => {
		setSelectedTQC2011_R01(row);
		setTQC2011_R01Info({ ...defaultTQC2011_R01Info, inspectType });
		setTQC2011_R01DetailList([]);
		setOriginalTQC2011_R01DetailList([]);
		const response = await getTQC2011_R01Detail({
			productResultNo: row.productResultNo,
			inspectType,
		});
		if (response.status >= 400 || response.data?.successFlag !== true) return;
		const result = response.data.data;
		if (!result) return;
		setDetailData(row, result);
		if ((result.detailList ?? []).length === 0) {
			showWarning(getNoDetailMessage(row, inspectType), 3000);
		}
	}, [
		setDetailData,
		setOriginalTQC2011_R01DetailList,
		setSelectedTQC2011_R01,
		setTQC2011_R01DetailList,
		setTQC2011_R01Info,
	]);

	const refreshHeaderList = useCallback(async () => {
		const params = getParams(TQC2011_R01Filter);
		const [listResponse, countResponse] = await Promise.all([
			getTQC2011_R01List("search", params),
			getTQC2011_R01AllCount(params),
		]);
		const list = listResponse.data.data ?? [];
		setTQC2011_R01List(list);
		setAllCount(countResponse.data.data ?? list.length);
		return list;
	}, [TQC2011_R01Filter, setAllCount, setTQC2011_R01List]);

	const handleHeaderRowClick = useCallback(async (row: TQC2011_R01HeaderDto) => {
		const fmlTypes = inspectionTypeList.filter((item) => item.ref01 === "FML");
		const currentType = fmlTypes.some((item) => item.minorCd === TQC2011_R01Info.inspectType)
			? TQC2011_R01Info.inspectType
			: fmlTypes[0]?.minorCd;
		if (!currentType) {
			showWarning("INSPECTION_TYPE 코드에 FML 검사구분이 등록되어 있지 않습니다.");
			return;
		}
		try {
			setLoadingSpinner(true);
			setDetailScrollTop(null);
			await loadDetail(row, currentType);
		} catch {
			showError("검사항목을 조회하지 못했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		TQC2011_R01Info.inspectType,
		inspectionTypeList,
		loadDetail,
		setDetailScrollTop,
		setLoadingSpinner,
	]);

	const fetchData = useCallback(async () => {
		try {
			setLoadingSpinner(true);
			setScrollTop(null);
			initSelectedTQC2011_R01();
			setTQC2011_R01Info(defaultTQC2011_R01Info);
			setTQC2011_R01DetailList([]);
			setOriginalTQC2011_R01DetailList([]);
			const list = await refreshHeaderList();
			setTQC2011_R01PastFilter(TQC2011_R01Filter);
			if (list.length === 0) {
				showWarning("조회된 생산실적이 없습니다.");
			} else {
				const firstRow = useTQC2011_R01Store.getState().TQC2011_R01List[0];
				if (firstRow) await handleHeaderRowClick(firstRow);
				showSuccess(`조회가 완료되었습니다. (총 ${list.length}건)`);
			}
		} catch {
			showError("생산실적 조회 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		TQC2011_R01Filter,
		handleHeaderRowClick,
		initSelectedTQC2011_R01,
		refreshHeaderList,
		setLoadingSpinner,
		setOriginalTQC2011_R01DetailList,
		setScrollTop,
		setTQC2011_R01DetailList,
		setTQC2011_R01Info,
		setTQC2011_R01PastFilter,
	]);

	const handleInspectTypeChange = useCallback(async (inspectType: string) => {
		if (!selectedTQC2011_R01 || inspectType === TQC2011_R01Info.inspectType) return;
		const hasChanges = TQC2011_R01DetailList.some((detail) => detail.isModified);
		if (hasChanges && !window.confirm("입력 중인 검사값이 있습니다. 검사구분을 변경할까요?")) return;
		try {
			setLoadingSpinner(true);
			await loadDetail(selectedTQC2011_R01, inspectType);
		} catch {
			showError("검사구분 변경 중 검사항목을 조회하지 못했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		TQC2011_R01DetailList,
		TQC2011_R01Info.inspectType,
		loadDetail,
		selectedTQC2011_R01,
		setLoadingSpinner,
	]);

	const handleSaveClick = useCallback(async () => {
		if (!selectedTQC2011_R01) return showWarning("저장할 생산실적을 선택해주세요.");
		if (!TQC2011_R01Info.inspectType) return showWarning("검사구분을 선택해주세요.");
		if (!TQC2011_R01Info.inspectorEmp) return showWarning("검사자를 입력해주세요.");
		if (!TQC2011_R01Info.inspectDt) return showWarning("검사일자를 입력해주세요.");
		if (TQC2011_R01DetailList.length === 0) {
			return showWarning(getNoDetailMessage(selectedTQC2011_R01, TQC2011_R01Info.inspectType), 3000);
		}
		const hasValue = TQC2011_R01DetailList.some((detail) =>
			TQC2011_R01_VALUE_FIELDS.some((field) => String(detail[field] ?? "").trim()),
		);
		if (!hasValue) return showWarning("검사값을 한 개 이상 입력해주세요.");

		const header: TQC2011_R01HeaderDto = {
			...selectedTQC2011_R01,
			...TQC2011_R01Info,
			inspectDt: toYmd(TQC2011_R01Info.inspectDt),
		};
		try {
			setLoadingSpinner(true);
			const response = await saveTQC2011_R01({ header, detailList: TQC2011_R01DetailList });
			if (response.status >= 400 || response.data?.successFlag !== true) return;
			await loadDetail(header, header.inspectType);
			await refreshHeaderList();
			showSuccess("공정검사 결과가 저장되었습니다.");
		} catch {
			showError("저장 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		TQC2011_R01DetailList,
		TQC2011_R01Info,
		loadDetail,
		refreshHeaderList,
		selectedTQC2011_R01,
		setLoadingSpinner,
	]);

	const handleDeleteClick = useCallback(async () => {
		if (!selectedTQC2011_R01?.inspectNo) return showWarning("삭제할 검사결과가 없습니다.");
		try {
			setLoadingSpinner(true);
			const response = await deleteTQC2011_R01(selectedTQC2011_R01);
			if (response.status >= 400 || response.data?.successFlag !== true) return;
			await loadDetail(selectedTQC2011_R01, TQC2011_R01Info.inspectType);
			await refreshHeaderList();
			showSuccess("공정검사 결과가 삭제되었습니다.");
		} catch {
			showError("삭제 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		TQC2011_R01Info.inspectType,
		loadDetail,
		refreshHeaderList,
		selectedTQC2011_R01,
		setLoadingSpinner,
	]);

	const handleExcelClick = useCallback(async () => {
		try {
			setLoadingSpinner(true);
			const response = await downloadExcel_TQC2011_R01(getParams(TQC2011_R01Filter));
			if (!response.data.data) return showWarning("엑셀로 내보낼 데이터가 없습니다.");
			downloadSettingExcel({
				responseData: response.data.data,
				fileName: "공정검사결과등록.xlsx",
			});
		} catch {
			showError("엑셀 다운로드 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC2011_R01Filter, setLoadingSpinner]);

	const handlePrintClick = useCallback(async () => {
		if (!selectedTQC2011_R01?.productResultNo || !TQC2011_R01Info.inspectType) {
			showWarning("출력할 공정검사 대상과 검사구분을 선택해주세요.");
			return;
		}
		try {
			setLoadingSpinner(true);
			const response = await downloadCertificatePdf_TQC2011_R01({
				productResultNo: selectedTQC2011_R01.productResultNo,
				inspectType: TQC2011_R01Info.inspectType,
			});
			const contentType = String(response.headers?.["content-type"] ?? "");
			if (response.status >= 400 || !contentType.includes("application/pdf")) {
				const detail = response.data instanceof Blob
					? (await response.data.text()).trim()
					: "";
				showError(detail || "공정검사 검사성적서 출력 중 오류가 발생했습니다.");
				return;
			}
			const fileUrl = URL.createObjectURL(
				new Blob([response.data], { type: "application/pdf" }),
			);
			window.open(fileUrl, "_blank");
			setTimeout(() => URL.revokeObjectURL(fileUrl), 5000);
		} catch (error) {
			const data = (error as { response?: { data?: unknown } })?.response?.data;
			const detail = data instanceof Blob ? (await data.text()).trim() : "";
			showError(detail || "공정검사 검사성적서 출력 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC2011_R01Info.inspectType, selectedTQC2011_R01, setLoadingSpinner]);

	const initContainerData = useCallback(() => initData(), [initData]);
	return {
		fetchData,
		handleHeaderRowClick,
		handleInspectTypeChange,
		handleSaveClick,
		handleDeleteClick,
		handleExcelClick,
		handlePrintClick,
		initContainerData,
	};
};
